/// <amd-module name="Feed/Legacy/Protocols/Npm/NpmDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { Package } from "Feed/Common/Contracts/Feed";
import { NpmBatchOperationType, NpmPackagesBatchRequest, Package as NpmPackage } from "Feed/Common/Protocols/Npm/Contracts/Npm";
import { INpmRestClient } from "Feed/Common/Protocols/Npm/RestClient/Npm";
import { MinimalPackageDetails, UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { getFullyQualifiedFeedId } from "Feed/Legacy/Helpers/FeedNameResolver";
import { BatchExecutor, IBatchIterator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import * as NpmPackageDetails from "Feed/Legacy/Protocols/Npm/NpmPackageDetails";
import { Feed, projId } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface INpmReadmeContent {
    packageName: string;
    packageVersion: string;
    rawReadmeContent: string;
}

export interface INpmDataService extends IVssService {
    batchSize: number;
    batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]>;
    batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]>;
    batchPromotePackages(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void[]>;
    batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]>;
    getReadmeUnscopedPackage(feed: Feed, packageName: string, packageVersion: string): Promise<INpmReadmeContent>;
    getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior>;
    setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void>;
    updatePackage(
        npmPackageVersionDetails: NpmPackageDetails.PackageVersionDetails,
        feed: Feed,
        packageName: string,
        packageVersion: string
    ): Promise<NpmPackage>;
    updatePackages(npmBatchRequest: NpmPackagesBatchRequest, feed: Feed): Promise<void>;
}

export class NpmDataService extends VssService implements INpmDataService {
    private _npmReadmeContent: INpmReadmeContent = null;

    get NpmRestClient(): INpmRestClient {
        return this.pageContext.getRestClient<INpmRestClient>("INpmRestClient");
    }

    get batchSize() {
        return this.pageContext
            .getService<HubWebPageDataService>("IFeedWebPageDataService")
            .getBatchOperationPageSize();
    }

    public updatePackages(npmBatchRequest: NpmPackagesBatchRequest, feed: Feed): Promise<void> {
        return this.NpmRestClient.updatePackages(npmBatchRequest, feed.id, projId(feed));
    }

    public updatePackage(
        npmPackageVersionDetails: NpmPackageDetails.PackageVersionDetails,
        feed: Feed,
        packageName: string,
        packageVersion: string
    ): Promise<NpmPackage> {
        return this.NpmRestClient.updatePackage(
            npmPackageVersionDetails,
            feed.id,
            packageName,
            packageVersion,
            projId(feed)
        );
    }

    public async getReadmeUnscopedPackage(
        feed: Feed,
        packageName: string,
        packageVersion: string
    ): Promise<INpmReadmeContent> {
        if (
            this._npmReadmeContent != null &&
            this._npmReadmeContent.packageName === packageName &&
            this._npmReadmeContent.packageVersion === packageVersion
        ) {
            return this._npmReadmeContent;
        }

        const feedId = getFullyQualifiedFeedId(feed);

        return this.NpmRestClient.getReadmeUnscopedPackage(feedId, packageName, packageVersion, projId(feed)).then(
            rawReadmeContent => {
                this._npmReadmeContent = {
                    packageName,
                    packageVersion,
                    rawReadmeContent
                };
                return this._npmReadmeContent;
            },
            err => {
                this._npmReadmeContent = {
                    packageName,
                    packageVersion,
                    rawReadmeContent: ""
                };
                return this._npmReadmeContent;
            }
        );
    }

    public batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NpmPackagesBatchRequest>{
                data: null,
                operation: NpmBatchOperationType.Unpublish,
                packages: packageDetails
            };
            await this.NpmRestClient.updatePackages(batchRequest, feed.id, projId(feed));
        });
    }

    public batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NpmPackagesBatchRequest>{
                data: null,
                operation: NpmBatchOperationType.RestoreToFeed,
                packages: packageDetails
            };
            await this.NpmRestClient.updateRecycleBinPackages(batchRequest, feed.id, projId(feed));
        });
    }

    public batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NpmPackagesBatchRequest>{
                data: null,
                operation: NpmBatchOperationType.PermanentDelete,
                packages: packageDetails
            };
            await this.NpmRestClient.updateRecycleBinPackages(batchRequest, feed.id, projId(feed));
        });
    }

    public batchPromotePackages(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NpmPackagesBatchRequest>{
                data: { viewId },
                operation: NpmBatchOperationType.Promote,
                packages: packageDetails
            };
            await this.NpmRestClient.updatePackages(batchRequest, feed.id, projId(feed));
        });
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.NpmRestClient.getUpstreamingBehavior(feed.id, pkg.name, projId(feed));
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.NpmRestClient.setUpstreamingBehavior(feed.id, pkg.name, behavior, projId(feed));
    }
}

Services.add("INpmDataService", { serviceFactory: NpmDataService });
